import React from 'react';

import { Badge } from '@schoolinks/ui-components';
import cn from 'classnames';
import { isEmpty, noop } from 'lodash';
import {
  bool,
  func,
  oneOf,
  oneOfType,
  number as num,
  shape,
  string,
} from 'prop-types';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';

import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';

import Chip from 'components/Common/Chip';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Text from 'components/Common/Text';
import Tooltip from 'components/Common/Tooltip';

import s from './Checkbox.css';

const styles = {
  small: s.small,
  medium: s.medium,
};

const Checkbox = ({
  size,
  label,
  value,
  checked,
  classes,
  disabled,
  onChange,
  hasBorder,
  hasError,
  errorText,
  description,
  indeterminate,
  checkBoxClasses,
  removeOffset,
  testId,
  badgeProps,
  chipProps,
  infoTooltipProps,
  'aria-label': ariaLabel,
}) => (
  <Flexbox flexDirection="column">
    <FormControlLabel
      label={
        <>
          {label || chipProps ? (
            <Flexbox alignItems="center">
              {chipProps ? (
                <Chip
                  color={chipProps.color}
                  isClickable={false}
                  label={chipProps.label}
                />
              ) : (
                <Typography
                  classes={{
                    root: s.label,
                  }}
                >
                  {label}
                </Typography>
              )}
              {badgeProps && (
                <Badge
                  classes={{ root: s.badgeRoot }}
                  color={badgeProps.color}
                  number={badgeProps.number}
                  variant={badgeProps.variant}
                />
              )}
              {!isEmpty(infoTooltipProps) && (
                <Tooltip
                  arrow
                  placement={infoTooltipProps?.placement || 'top'}
                  title={infoTooltipProps?.title}
                >
                  <span className={s.infoIcon}>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      size={size === 'small' ? 'xs' : 'sm'}
                      color={disabled ? 'gray' : 'grayDark'}
                    />
                  </span>
                </Tooltip>
              )}
            </Flexbox>
          ) : null}
          {description ? (
            <Typography variant="body2" classes={{ root: s.description }}>
              {description}
            </Typography>
          ) : null}
        </>
      }
      control={
        <MuiCheckbox
          size={size}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          indeterminate={indeterminate}
          aria-label={ariaLabel}
          classes={{
            root: s.checkbox,
            checked: s.checked,
            ...checkBoxClasses,
          }}
          inputProps={{
            'data-testid': testId,
          }}
        />
      }
      classes={{
        ...classes,
        root: cn(
          { [s.disabled]: disabled },
          s.root,
          classes.root,
          styles[size],
          {
            [s.border]: hasBorder && label,
            [s.alignStart]: description,
            [s.checked]: checked,
            [s.error]: hasError,
            [s.removeOffset]: removeOffset,
          },
        ),
        label: classes?.label ?? s.labelDescription,
      }}
    />
    {hasError && errorText ? (
      <Text variant="meta" color="redDark">
        {errorText}
      </Text>
    ) : null}
  </Flexbox>
);

Checkbox.propTypes = {
  label: string,
  disabled: bool,
  hasBorder: bool,
  hasError: bool,
  classes: shape({
    root: string,
  }),
  indeterminate: bool,
  description: string,
  checked: bool.isRequired,
  onChange: func,
  checkBoxClasses: shape({}),
  value: oneOfType([string, num, shape({})]),
  size: oneOf(['small', 'medium']),
  removeOffset: bool,
  errorText: string,
  testId: string,
  badgeProps: shape({
    number: num,
    color: oneOf([
      'red',
      'gray',
      'blue',
      'green',
      'orange',
      'yellow',
      'purple',
      'blue-steel',
      'disabled',
    ]),
    variant: oneOf(['filled', 'text']),
  }),
  chipProps: shape({
    label: string,
    color: oneOf([
      'orange',
      'yellow',
      'violet',
      'teal',
      'indigo',
      'blueSteel',
      'green',
    ]),
  }),
  infoTooltipProps: shape({
    title: string,
    placement: oneOf(['top', 'left', 'bottom', 'right']),
  }),
  'aria-label': string,
};

Checkbox.defaultProps = {
  value: undefined,
  label: '',
  classes: {
    root: undefined,
  },
  size: 'medium',
  description: '',
  disabled: false,
  hasBorder: false,
  hasError: false,
  errorText: null,
  checkBoxClasses: {},
  indeterminate: false,
  removeOffset: false,
  testId: null,
  badgeProps: undefined,
  chipProps: undefined,
  infoTooltipProps: undefined,
  onChange: noop,
  'aria-label': '',
};

export default Checkbox;
